import { useCallback, useEffect, useState } from "react";
import { PackagePlus, ClipboardList, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { Kpi } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExportButtons } from "@/components/ExportButtons";
import { BackdateField } from "@/components/BackdateField";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const short = (n) => (n || "").replace("Veloskin ", "");

const ROW_TINT = {
  out: "bg-rose-50/70 hover:bg-rose-50",
  low: "bg-amber-50/60 hover:bg-amber-50",
  ok: "hover:bg-brand-tint/60",
};

export const StockOverview = ({ canWrite, canHqWrite, canClear }) => {
  const [data, setData] = useState(null);
  const [moves, setMoves] = useState(null);
  const [orders, setOrders] = useState(null);
  const [hq, setHq] = useState(null);
  const [hqForm, setHqForm] = useState({ quantity: "", unit_cost: "", threshold: "" });
  const EMPTY_PURCHASE = { sku_id: "", quantity: "", cost_price: "", gst_rate: "18",
    purchase_date: new Date().toISOString().slice(0, 10), supplier: "", invoice_no: "" };
  const [purchase, setPurchase] = useState(null);
  const [savingP, setSavingP] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearText, setClearText] = useState("");
  const [batches, setBatches] = useState(null);   // { name, unit, list }
  const [hqPurchases, setHqPurchases] = useState([]);
  const [pFilter, setPFilter] = useState({ product: "all", supplier: "all", from: "", to: "" });
  const EMPTY_ORDER = { order_type: "studio", studio_id: "", b2b_customer_name: "",
    b2b_customer_contact: "", lines: [{ sku_id: "", quantity: "", price: "" }] };
  const [order, setOrder] = useState(null);
  const [savingO, setSavingO] = useState(false);
  const addLine = () => setOrder((o) => ({ ...o, lines: [...o.lines, { sku_id: "", quantity: "", price: "" }] }));
  const removeLine = (i) => setOrder((o) => ({ ...o,
    lines: o.lines.length > 1 ? o.lines.filter((_, idx) => idx !== i) : o.lines }));
  const setLine = (i, k, v) => setOrder((o) => ({ ...o,
    lines: o.lines.map((ln, idx) => (idx === i ? { ...ln, [k]: v } : ln)) }));
  const [detailers, setDetailers] = useState([]);
  useEffect(() => {
    api.get("/b2b/detailers").then(({ data }) => setDetailers(data)).catch(() => setDetailers([]));
  }, []);

  const loadPurchases = useCallback(() => {
    if (!canHqWrite) return;
    api.get("/hq/purchases").then(({ data }) => setHqPurchases(data)).catch(() => setHqPurchases([]));
  }, [canHqWrite]);
  useEffect(() => { loadPurchases(); }, [loadPurchases]);

  const saveOrder = async () => {
    if (order.order_type === "studio" && !order.studio_id) return toast.error("Select a studio");
    if (order.order_type === "b2b" && !order.b2b_customer_name.trim()) return toast.error("Enter the B2B customer");
    const valid = (order.lines || []).filter((l) => l.sku_id && Number(l.quantity) > 0);
    if (valid.length === 0) return toast.error("Add at least one product with a quantity");
    setSavingO(true);
    try {
      await api.post("/accountant/orders", {
        order_type: order.order_type, studio_id: order.studio_id || null,
        b2b_customer_name: order.b2b_customer_name, b2b_customer_contact: order.b2b_customer_contact,
        items: valid.map((l) => ({ sku_id: l.sku_id, quantity: Number(l.quantity),
                                   price: l.price !== "" ? Number(l.price) : null })),
      });
      toast.success("Order created");
      setOrder(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingO(false); }
  };

  const openBatches = async (r) => {
    setBatches({ name: r.name, unit: r.unit, list: null });
    try {
      const { data } = await api.get(`/catalog/${r.sku_id}/purchases`);
      setBatches({ name: r.name, unit: r.unit, list: data });
    } catch (e) {
      setBatches({ name: r.name, unit: r.unit, list: [] });
    }
  };

  const load = useCallback(() => {
    api
      .get("/stock/overview")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openMoves = async (row) => {
    setMoves({ loading: true, sku: row.name });
    try {
      const { data } = await api.get("/stock/movements", { params: { sku: row.name } });
      setMoves(data);
    } catch (e) {
      setMoves(null);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openOrders = async (p) => {
    setOrders({ loading: true, studio: p });
    try {
      const { data } = await api.get("/material-orders", { params: { studio_id: p.studio_id } });
      setOrders({ studio: p, rows: data });
    } catch (e) {
      setOrders(null);
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openHq = (row) => {
    setHq(row);
    setHqForm({ quantity: "", unit_cost: "", threshold: String(row.hq_low_threshold ?? 0), entry_date: "" });
  };

  const saveHq = async () => {
    try {
      if (Number(hqForm.quantity) > 0) {
        await api.post(`/catalog/${hq.sku_id}/receipt`, {
          quantity: Number(hqForm.quantity),
          unit_cost: hqForm.unit_cost === "" ? null : Number(hqForm.unit_cost),
          entry_date: hqForm.entry_date || null,
        });
      }
      if (Number(hqForm.threshold) !== Number(hq.hq_low_threshold)) {
        await api.put(`/catalog/${hq.sku_id}/hq-stock`, {
          hq_quantity: hq.hq_quantity + Number(hqForm.quantity || 0),
          hq_low_threshold: Number(hqForm.threshold || 0),
        });
      }
      toast.success(`HQ stock updated — ${hq.name}`);
      setHq(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const savePurchase = async () => {
    if (!purchase.sku_id) return toast.error("Select a product");
    if (!(Number(purchase.quantity) > 0)) return toast.error("Enter a quantity");
    if (!(Number(purchase.cost_price) >= 0)) return toast.error("Enter the cost price (excl. GST)");
    setSavingP(true);
    try {
      const { data: res } = await api.post(`/catalog/${purchase.sku_id}/purchase`, {
        quantity: Number(purchase.quantity), cost_price: Number(purchase.cost_price),
        gst_rate: Number(purchase.gst_rate), purchase_date: purchase.purchase_date,
        supplier: purchase.supplier, invoice_no: purchase.invoice_no,
      });
      toast.success(`Purchase logged · total GST ₹${res.total_gst_payable.toLocaleString("en-IN")}`);
      setPurchase(null);
      load(); loadPurchases();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingP(false); }
  };

  const clearStock = async () => {
    try {
      const { data: res } = await api.post("/hq/clear-stock", { confirm: "CLEAR" });
      toast.success(`HQ warehouse cleared — ${res.skus_reset} SKUs reset to zero`);
      setClearOpen(false); setClearText("");
      load(); loadPurchases();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!data) return null;
  const { summary, studios, rows, purchases, top_buyer_month, top_buyer_all } = data;

  return (
    <div className="mb-8 space-y-6" data-testid="stock-overview">
      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi label="Total SKUs" value={summary.total_skus} testid="stock-total-skus" />
        <Kpi
          label="Low stock"
          value={summary.low_stock}
          accent="text-amber-600"
          hint="At or below the reorder level somewhere"
          testid="stock-low-count"
        />
        <Kpi
          label="Out of stock"
          value={summary.out_of_stock}
          accent="text-rose-600"
          hint="Nothing left at HQ or any studio"
          testid="stock-out-count"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="border-brand-line bg-brand-tint/40 shadow-none">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Top studio buyer · {data.month}
            </p>
            <p
              className="font-display mt-2 text-2xl font-semibold text-brand-ink"
              data-testid="top-buyer-month-name"
            >
              {top_buyer_month ? top_buyer_month.studio_name : "No purchases this month"}
            </p>
            <p className="mt-1 text-sm text-slate-600" data-testid="top-buyer-month-value">
              {money(top_buyer_month?.month_value || 0)} purchased at HQ billing price
            </p>
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Top studio buyer · since inception
            </p>
            <p
              className="font-display mt-2 text-2xl font-semibold text-brand-ink"
              data-testid="top-buyer-all-name"
            >
              {top_buyer_all ? top_buyer_all.studio_name : "No purchases yet"}
            </p>
            <p className="mt-1 text-sm text-slate-600" data-testid="top-buyer-all-value">
              {money(top_buyer_all?.total_value || 0)} purchased at HQ billing price
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Stock position</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Units held at the HQ warehouse and at each studio. Amber = at or below the reorder
              level, red = out of stock. Click a row for its full movement history.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canHqWrite && (
              <>
                <Button size="sm" variant="outline" data-testid="create-order-btn"
                  onClick={() => setOrder({ ...EMPTY_ORDER })}>
                  <PackagePlus className="mr-1.5 h-4 w-4" /> Create Order
                </Button>
                <Button size="sm" data-testid="log-purchase-btn"
                  onClick={() => setPurchase({ ...EMPTY_PURCHASE })}>
                  <ClipboardList className="mr-1.5 h-4 w-4" /> Log Purchase
                </Button>
              </>
            )}
            {canClear && (
              <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50"
                data-testid="clear-stock-btn" onClick={() => setClearOpen(true)}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Clear All Stock
              </Button>
            )}
            <ExportButtons
              testidPrefix="stock-position"
              filename="veloskin-stock-position"
              title="Stock Position"
              subtitle="HQ warehouse and studio quantities"
              columns={[
                { label: "Material", value: (r) => r.name },
                { label: "Unit", value: (r) => r.unit },
                { label: "HQ", value: (r) => r.hq_quantity },
                ...studios.map((s) => ({
                  label: s.name,
                  value: (r) => r.by_studio[s.id]?.quantity ?? 0,
                })),
              ]}
              rows={rows}
            />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[900px] text-sm" data-testid="stock-position-table">
            <thead>
              <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="sticky left-0 bg-white p-3">Material</th>
                <th className="p-3">Unit</th>
                <th className="p-3 text-right">HQ warehouse</th>
                {studios.map((s) => (
                  <th key={s.id} className="p-3 text-right font-medium">
                    {short(s.name)}
                    <span className="block text-[10px] normal-case text-slate-400">{s.city}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={studios.length + 3} className="p-10 text-center text-slate-500">
                    No SKUs in the catalog yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.name}
                  onClick={() => openMoves(r)}
                  className={`cursor-pointer border-b border-slate-100 transition-colors duration-150 ${ROW_TINT[r.status]}`}
                  data-testid={`position-row-${r.name}`}
                >
                  <td className="sticky left-0 bg-inherit p-3 font-medium text-brand-ink">
                    {r.name}
                    <span className="block text-xs text-slate-500">
                      {r.category}
                      {Number(r.purchase_unit_size || 1) !== 1 && (
                        <span className="text-slate-400">
                          {" "}· 1 {r.unit} = {r.purchase_unit_size} {r.consumption_unit}
                        </span>
                      )}
                      {r.status !== "ok" && (
                        <Badge
                          data-testid={`position-flag-${r.name}`}
                          className={`ml-2 border-0 ${
                            r.status === "out"
                              ? "bg-rose-100 text-rose-700"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {r.status === "out" ? "Out of stock" : "Low stock"}
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500">{r.unit}</td>
                  <td className="p-3 text-right tabular-nums" data-testid={`position-hq-${r.name}`}>
                    <span
                      className={
                        r.hq_out ? "font-semibold text-rose-600" : r.hq_low ? "font-semibold text-amber-600" : "text-slate-800"
                      }
                    >
                      {r.hq_quantity.toFixed(2)}
                    </span>
                    {canWrite && r.sku_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-1 h-7 px-1.5"
                        data-testid={`hq-receipt-btn-${r.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openHq(r);
                        }}
                      >
                        <PackagePlus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {r.sku_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-0.5 h-7 px-1.5"
                        title="View FIFO batches"
                        data-testid={`hq-batches-btn-${r.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openBatches(r);
                        }}
                      >
                        <Layers className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                  {studios.map((s) => {
                    const cell = r.by_studio[s.id];
                    return (
                      <td
                        key={s.id}
                        className={`p-3 text-right tabular-nums ${
                          !cell
                            ? "text-slate-300"
                            : cell.out
                              ? "font-semibold text-rose-600"
                              : cell.low
                                ? "font-semibold text-amber-600"
                                : "text-slate-700"
                        }`}
                        data-testid={`position-${r.name}-${s.id}`}
                      >
                        {cell ? cell.quantity.toFixed(2) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Studio purchases from HQ</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Delivered material orders at HQ billing price. Click a studio for its order history.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[720px] text-sm" data-testid="studio-purchases-table">
            <thead>
              <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="p-3">Studio</th>
                <th className="p-3 text-right">Units (month)</th>
                <th className="p-3 text-right">Units (cumulative)</th>
                <th className="p-3 text-right">Value (month)</th>
                <th className="p-3 text-right">Value (cumulative)</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-10 text-center text-slate-500">
                    No studios yet.
                  </td>
                </tr>
              )}
              {purchases.map((p) => (
                <tr
                  key={p.studio_id}
                  onClick={() => openOrders(p)}
                  className="cursor-pointer border-b border-slate-100 hover:bg-brand-tint/60"
                  data-testid={`purchase-row-${p.studio_id}`}
                >
                  <td className="p-3 font-medium text-brand-ink">
                    {p.studio_name}
                    <span className="block text-xs text-slate-500">
                      {p.city} · {p.total_orders} delivered order(s)
                    </span>
                  </td>
                  <td className="p-3 text-right tabular-nums">{p.month_units.toFixed(2)}</td>
                  <td className="p-3 text-right tabular-nums">{p.total_units.toFixed(2)}</td>
                  <td className="p-3 text-right tabular-nums" data-testid={`purchase-month-value-${p.studio_id}`}>
                    {money(p.month_value)}
                  </td>
                  <td className="p-3 text-right font-semibold tabular-nums" data-testid={`purchase-total-value-${p.studio_id}`}>
                    {money(p.total_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!moves} onOpenChange={(o) => !o && setMoves(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle>Movement history — {moves?.sku}</DialogTitle>
          </DialogHeader>
          {moves?.loading && <p className="text-sm text-slate-500">Loading…</p>}
          {moves && !moves.loading && (
            <>
              <p className="text-xs text-slate-500">
                Total in {(moves.totals?.in ?? 0).toFixed(2)} {moves.unit} · total out{" "}
                {(moves.totals?.out ?? 0).toFixed(2)} {moves.unit}
              </p>
              <table className="w-full text-sm" data-testid="movement-table">
                <thead>
                  <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2">Date</th>
                    <th className="py-2">Location</th>
                    <th className="py-2">Movement</th>
                    <th className="py-2 text-right">Qty</th>
                    <th className="py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(moves.movements || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-500">
                        No movement recorded for this material yet.
                      </td>
                    </tr>
                  )}
                  {(moves.movements || []).map((m, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 whitespace-nowrap">{fmtDate(m.date)}</td>
                      <td className="py-2">{m.location}</td>
                      <td className="py-2 text-slate-600">
                        {m.type}
                        {m.reference && (
                          <span className="block text-xs text-slate-400">{m.reference}</span>
                        )}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums font-medium ${
                          m.quantity < 0 ? "text-rose-600" : "text-emerald-600"
                        }`}
                      >
                        {m.quantity > 0 ? "+" : ""}
                        {m.quantity.toFixed(2)}
                      </td>
                      <td className="py-2 text-right tabular-nums text-slate-600">
                        {money(m.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!orders} onOpenChange={(o) => !o && setOrders(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="studio-orders-dialog">
          <DialogHeader>
            <DialogTitle>Order history — {orders?.studio?.studio_name}</DialogTitle>
          </DialogHeader>
          {orders?.loading && <p className="text-sm text-slate-500">Loading…</p>}
          {orders && !orders.loading && (
            <table className="w-full text-sm" data-testid="studio-orders-table">
              <thead>
                <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2">Date</th>
                  <th className="py-2">Items</th>
                  <th className="py-2 text-right">Units</th>
                  <th className="py-2 text-right">Value</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(orders.rows || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-500">
                      This studio has not ordered any material yet.
                    </td>
                  </tr>
                )}
                {(orders.rows || []).map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 align-top">
                    <td className="py-2 whitespace-nowrap">{fmtDate(o.delivered_at || o.created_at)}</td>
                    <td className="py-2 text-slate-600">
                      {(o.items || []).map((l, i) => (
                        <span key={i} className="block text-xs">
                          {l.name} — {l.quantity} {l.unit} @ {money(l.hq_sale_price)}
                        </span>
                      ))}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {(o.items || []).reduce((a, l) => a + (l.quantity || 0), 0).toFixed(2)}
                    </td>
                    <td className="py-2 text-right tabular-nums font-medium">{money(o.revenue)}</td>
                    <td className="py-2">
                      <Badge className="border-0 bg-slate-100 text-slate-700">{o.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!hq} onOpenChange={(o) => !o && setHq(null)}>
        <DialogContent className="sm:max-w-md" data-testid="hq-receipt-dialog">
          <DialogHeader>
            <DialogTitle>HQ warehouse — {hq?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            On hand: {(hq?.hq_quantity ?? 0).toFixed(2)} {hq?.unit}. Record an inbound goods receipt
            or change the reorder level.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Quantity received ({hq?.unit})</Label>
              <Input
                className="min-h-[44px]"
                inputMode="decimal"
                data-testid="hq-receipt-quantity-input"
                value={hqForm.quantity}
                onChange={(e) => setHqForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </div>
            <div>
              <Label>HQ cost per unit (₹) — optional</Label>
              <Input
                className="min-h-[44px]"
                inputMode="decimal"
                data-testid="hq-receipt-cost-input"
                value={hqForm.unit_cost}
                onChange={(e) => setHqForm((f) => ({ ...f, unit_cost: e.target.value }))}
              />
            </div>
            <div>
              <Label>HQ reorder level</Label>
              <Input
                className="min-h-[44px]"
                inputMode="decimal"
                data-testid="hq-threshold-input"
                value={hqForm.threshold}
                onChange={(e) => setHqForm((f) => ({ ...f, threshold: e.target.value }))}
              />
            </div>
          </div>
          <BackdateField
            value={hqForm?.entry_date}
            onChange={(v) => setHqForm((f) => ({ ...f, entry_date: v }))}
          />
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setHq(null)}>
              Cancel
            </Button>
            <Button className="min-h-[44px]" data-testid="save-hq-receipt-btn" onClick={saveHq}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Purchase */}
      <Dialog open={!!purchase} onOpenChange={(o) => !o && setPurchase(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="log-purchase-dialog">
          <DialogHeader><DialogTitle>Log HQ stock purchase</DialogTitle></DialogHeader>
          {purchase && (() => {
            const cp = Number(purchase.cost_price) || 0;
            const gr = Number(purchase.gst_rate) || 0;
            const qty = Number(purchase.quantity) || 0;
            const gstPerUnit = +(cp * gr / 100).toFixed(2);
            const inclUnit = +(cp + gstPerUnit).toFixed(2);
            const totalGst = +(gstPerUnit * qty).toFixed(2);
            return (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Product</Label>
                  <Select value={purchase.sku_id} onValueChange={(v) => setPurchase((p) => ({ ...p, sku_id: v }))}>
                    <SelectTrigger className="mt-1 min-h-[44px]" data-testid="purchase-sku">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {rows.filter((r) => r.sku_id).map((r) => (
                        <SelectItem key={r.sku_id} value={r.sku_id}>{r.name} ({r.unit})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Quantity purchased</Label>
                    <Input className="mt-1 min-h-[44px]" inputMode="decimal" data-testid="purchase-qty"
                      value={purchase.quantity} onChange={(e) => setPurchase((p) => ({ ...p, quantity: e.target.value }))} /></div>
                  <div><Label className="text-xs">Cost / unit (excl. GST) ₹</Label>
                    <Input className="mt-1 min-h-[44px]" inputMode="decimal" data-testid="purchase-cost"
                      value={purchase.cost_price} onChange={(e) => setPurchase((p) => ({ ...p, cost_price: e.target.value }))} /></div>
                  <div><Label className="text-xs">GST rate</Label>
                    <Select value={purchase.gst_rate} onValueChange={(v) => setPurchase((p) => ({ ...p, gst_rate: v }))}>
                      <SelectTrigger className="mt-1 min-h-[44px]" data-testid="purchase-gst"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["5", "12", "18", "28"].map((g) => <SelectItem key={g} value={g}>{g}%</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Purchase date</Label>
                    <Input type="date" className="mt-1 min-h-[44px]" value={purchase.purchase_date}
                      onChange={(e) => setPurchase((p) => ({ ...p, purchase_date: e.target.value }))} /></div>
                  <div><Label className="text-xs">Supplier (optional)</Label>
                    <Input className="mt-1 min-h-[44px]" value={purchase.supplier}
                      onChange={(e) => setPurchase((p) => ({ ...p, supplier: e.target.value }))} /></div>
                  <div><Label className="text-xs">Invoice no. (optional)</Label>
                    <Input className="mt-1 min-h-[44px]" value={purchase.invoice_no}
                      onChange={(e) => setPurchase((p) => ({ ...p, invoice_no: e.target.value }))} /></div>
                </div>
                <div className="rounded-md bg-brand-tint/60 p-3 text-sm" data-testid="purchase-gst-summary">
                  <div className="flex justify-between"><span className="text-slate-500">GST per unit</span><span>{money(gstPerUnit)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Cost incl. GST per unit</span><span>{money(inclUnit)}</span></div>
                  <div className="flex justify-between font-semibold text-brand-ink"><span>Total GST payable (info only)</span><span data-testid="purchase-total-gst">{money(totalGst)}</span></div>
                  <p className="mt-1 text-[11px] text-slate-400">Base cost stored excl. GST. GST shown for input-tax tracking only.</p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchase(null)}>Cancel</Button>
            <Button data-testid="save-purchase-btn" disabled={savingP} onClick={savePurchase}>Log purchase</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Stock */}
      <Dialog open={clearOpen} onOpenChange={(o) => { if (!o) { setClearOpen(false); setClearText(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="clear-stock-dialog">
          <DialogHeader><DialogTitle>Clear all HQ warehouse stock</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            This resets <b>every HQ warehouse quantity to zero</b> and removes all purchase batches.
            Studio stock is not affected. This cannot be undone. Type <b>CLEAR</b> to confirm.
          </p>
          <Input className="min-h-[44px]" data-testid="clear-stock-confirm" placeholder="CLEAR"
            value={clearText} onChange={(e) => setClearText(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClearOpen(false); setClearText(""); }}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={clearText !== "CLEAR"}
              data-testid="confirm-clear-stock-btn" onClick={clearStock}>Reset HQ stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FIFO batches per SKU */}
      <Dialog open={!!batches} onOpenChange={(o) => !o && setBatches(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="batches-dialog">
          <DialogHeader><DialogTitle>Purchase batches — {batches?.name}</DialogTitle></DialogHeader>
          {batches?.list === null ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
          ) : (batches?.list || []).length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500" data-testid="batches-empty">
              No purchase batches logged for this product yet.
            </p>
          ) : (() => {
            const active = batches.list.filter((b) => (b.qty_remaining || 0) > 0)
              .sort((a, b) => {
                const ka = `${a.purchase_date || ""}|${a.created_at || ""}`;
                const kb = `${b.purchase_date || ""}|${b.created_at || ""}`;
                return ka < kb ? -1 : ka > kb ? 1 : 0;
              });
            const depleted = batches.list.filter((b) => (b.qty_remaining || 0) <= 0);
            const currentId = active[0]?.id;
            const Row = ({ b, dim }) => (
              <div className={`flex items-center justify-between rounded-md border p-2.5 text-sm ${dim ? "border-slate-100 text-slate-400" : "border-brand-line"}`}
                data-testid={`batch-row-${b.id}`}>
                <div>
                  <div className="font-medium">{fmtDate(b.purchase_date)}
                    {b.id === currentId && (
                      <Badge className="ml-2 border-0 bg-emerald-100 text-emerald-700" data-testid="batch-current">Currently selling</Badge>
                    )}
                    {dim && <span className="ml-2 text-xs">depleted</span>}
                  </div>
                  <div className="text-xs text-slate-400">
                    {b.supplier ? `${b.supplier} · ` : ""}{b.invoice_no ? `Inv ${b.invoice_no} · ` : ""}
                    purchased {b.qty_purchased} {batches.unit}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-brand-ink">{money(b.cost_price)}<span className="text-xs font-normal text-slate-400">/{batches.unit}</span></div>
                  <div className="text-xs">{(b.qty_remaining || 0).toFixed(2)} {batches.unit} left</div>
                </div>
              </div>
            );
            return (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">COGS is drawn oldest-first (FIFO). The top batch is the cost currently applied.</p>
                {active.map((b) => <Row key={b.id} b={b} />)}
                {depleted.map((b) => <Row key={b.id} b={b} dim />)}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      {canHqWrite && (() => {
        const products = [...new Set(hqPurchases.map((p) => p.product).filter(Boolean))].sort();
        const suppliers = [...new Set(hqPurchases.map((p) => p.supplier).filter(Boolean))].sort();
        const filtered = hqPurchases.filter((p) => {
          if (pFilter.product !== "all" && p.product !== pFilter.product) return false;
          if (pFilter.supplier !== "all" && p.supplier !== pFilter.supplier) return false;
          if (pFilter.from && (p.date || "") < pFilter.from) return false;
          if (pFilter.to && (p.date || "") > pFilter.to) return false;
          return true;
        });
        const grand = filtered.reduce((s, p) => s + (p.total_cost || 0), 0);
        const active = pFilter.product !== "all" || pFilter.supplier !== "all" || pFilter.from || pFilter.to;
        return (
        <Card className="mt-6 border-brand-line shadow-none" data-testid="hq-purchase-history">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">HQ Purchase History</CardTitle>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Product</Label>
                <Select value={pFilter.product} onValueChange={(v) => setPFilter((f) => ({ ...f, product: v }))}>
                  <SelectTrigger className="mt-0.5 h-9 w-40" data-testid="purchase-filter-product"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All products</SelectItem>
                    {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Supplier</Label>
                <Select value={pFilter.supplier} onValueChange={(v) => setPFilter((f) => ({ ...f, supplier: v }))}>
                  <SelectTrigger className="mt-0.5 h-9 w-40" data-testid="purchase-filter-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All suppliers</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">From</Label>
                <Input type="date" className="mt-0.5 h-9 w-36" data-testid="purchase-filter-from"
                  value={pFilter.from} onChange={(e) => setPFilter((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">To</Label>
                <Input type="date" className="mt-0.5 h-9 w-36" data-testid="purchase-filter-to"
                  value={pFilter.to} onChange={(e) => setPFilter((f) => ({ ...f, to: e.target.value }))} />
              </div>
              {active && (
                <Button variant="outline" size="sm" className="h-9" data-testid="purchase-filter-clear"
                  onClick={() => setPFilter({ product: "all", supplier: "all", from: "", to: "" })}>
                  Clear
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-brand-line text-left text-xs text-slate-500">
                <th className="p-2.5">Date</th><th className="p-2.5">Product</th>
                <th className="p-2.5 text-right">Qty</th><th className="p-2.5 text-right">Cost/unit (ex-GST)</th>
                <th className="p-2.5 text-right">GST</th><th className="p-2.5 text-right">Total</th>
                <th className="p-2.5">Supplier</th><th className="p-2.5">Invoice</th></tr></thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-500" data-testid="purchase-history-empty">
                    {hqPurchases.length === 0 ? "No purchases logged yet." : "No purchases match the current filters."}
                  </td></tr>
                )}
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-brand-line/60" data-testid={`purchase-row-${p.id}`}>
                    <td className="p-2.5">{fmtDate(p.date)}</td>
                    <td className="p-2.5 font-medium">{p.product}</td>
                    <td className="p-2.5 text-right">{p.quantity} {p.unit}</td>
                    <td className="p-2.5 text-right">{money(p.cost_price)}</td>
                    <td className="p-2.5 text-right">{money(p.gst_amount)} <span className="text-xs text-slate-400">({p.gst_rate}%)</span></td>
                    <td className="p-2.5 text-right font-semibold">{money(p.total_cost)}</td>
                    <td className="p-2.5 text-slate-500">{p.supplier || "—"}</td>
                    <td className="p-2.5 text-slate-500">{p.invoice_no || "—"}</td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot><tr className="border-t border-brand-line text-xs font-semibold">
                  <td className="p-2.5" colSpan={5} data-testid="purchase-history-count">
                    {filtered.length} purchase{filtered.length === 1 ? "" : "s"}
                  </td>
                  <td className="p-2.5 text-right" data-testid="purchase-history-total">{money(grand)}</td>
                  <td className="p-2.5" colSpan={2} />
                </tr></tfoot>
              )}
            </table>
          </CardContent>
        </Card>
        );
      })()}

      {/* Create Order (Accountant) */}
      <Dialog open={!!order} onOpenChange={(o) => !o && setOrder(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="create-order-dialog">
          <DialogHeader><DialogTitle>Create order</DialogTitle></DialogHeader>
          {order && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-brand-tint p-1">
                {["studio", "b2b"].map((t) => (
                  <button key={t} data-testid={`order-type-${t}`} onClick={() => setOrder((o) => ({ ...o, order_type: t }))}
                    className={`rounded-md py-2 text-xs font-semibold uppercase ${order.order_type === t ? "bg-brand text-white" : "text-brand"}`}>
                    {t === "studio" ? "Studio Order" : "B2B Order"}
                  </button>
                ))}
              </div>
              {order.order_type === "studio" ? (
                <div><Label className="text-xs">Studio</Label>
                  <Select value={order.studio_id} onValueChange={(v) => setOrder((o) => ({ ...o, studio_id: v }))}>
                    <SelectTrigger className="mt-1 min-h-[44px]" data-testid="order-studio"><SelectValue placeholder="Select studio" /></SelectTrigger>
                    <SelectContent>{studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Detailer (accredited or non-accredited)</Label>
                    <Select
                      value={order.b2b_detailer_key || ""}
                      onValueChange={(v) => {
                        if (v === "__other__") {
                          setOrder((o) => ({ ...o, b2b_detailer_key: v, b2b_customer_name: "", b2b_customer_contact: "" }));
                        } else {
                          const d = detailers.find((x) => `${x.type}:${x.id}` === v);
                          setOrder((o) => ({ ...o, b2b_detailer_key: v,
                            b2b_customer_name: d?.name || "", b2b_customer_contact: d?.contact || "" }));
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 min-h-[44px]" data-testid="order-b2b-detailer">
                        <SelectValue placeholder="Select a detailer" />
                      </SelectTrigger>
                      <SelectContent>
                        {detailers.filter((d) => d.type === "accredited").length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Accredited detailers</SelectLabel>
                            {detailers.filter((d) => d.type === "accredited").map((d) => (
                              <SelectItem key={`${d.type}:${d.id}`} value={`${d.type}:${d.id}`}>{d.name}</SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        {detailers.filter((d) => d.type === "prospect").length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Non-accredited detailers</SelectLabel>
                            {detailers.filter((d) => d.type === "prospect").map((d) => (
                              <SelectItem key={`${d.type}:${d.id}`} value={`${d.type}:${d.id}`}>{d.name}</SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        <SelectItem value="__other__">Other — type a name</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {order.b2b_detailer_key === "__other__" && (
                    <div><Label className="text-xs">B2B customer name</Label>
                      <Input className="mt-1 min-h-[44px]" data-testid="order-b2b-name" value={order.b2b_customer_name}
                        onChange={(e) => setOrder((o) => ({ ...o, b2b_customer_name: e.target.value }))} /></div>
                  )}
                  <div><Label className="text-xs">Contact (optional)</Label>
                    <Input className="mt-1 min-h-[44px]" data-testid="order-b2b-contact" value={order.b2b_customer_contact}
                      onChange={(e) => setOrder((o) => ({ ...o, b2b_customer_contact: e.target.value }))} /></div>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Products</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="order-add-line"
                    onClick={addLine}>
                    <PackagePlus className="mr-1.5 h-3.5 w-3.5" /> Add product
                  </Button>
                </div>
                {(order.lines || []).map((ln, i) => (
                  <div key={i} className="rounded-lg border border-brand-line p-2" data-testid={`order-line-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <Select value={ln.sku_id} onValueChange={(v) => setLine(i, "sku_id", v)}>
                          <SelectTrigger className="min-h-[44px]" data-testid={`order-sku-${i}`}>
                            <SelectValue placeholder="Select product" />
                          </SelectTrigger>
                          <SelectContent>
                            {rows.filter((r) => r.sku_id).map((r) => (
                              <SelectItem key={r.sku_id} value={r.sku_id}>{r.name} ({r.unit})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {order.lines.length > 1 && (
                        <Button type="button" size="icon" variant="ghost"
                          className="mt-0.5 shrink-0 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          data-testid={`order-remove-line-${i}`} onClick={() => removeLine(i)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <div><Label className="text-[11px] text-slate-500">Quantity</Label>
                        <Input className="mt-1 min-h-[44px]" inputMode="decimal" data-testid={`order-qty-${i}`}
                          value={ln.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} /></div>
                      <div><Label className="text-[11px] text-slate-500">Price / unit (ex-GST, optional)</Label>
                        <Input className="mt-1 min-h-[44px]" inputMode="decimal" data-testid={`order-price-${i}`}
                          value={ln.price} onChange={(e) => setLine(i, "price", e.target.value)} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrder(null)}>Cancel</Button>
            <Button data-testid="save-order-btn" disabled={savingO} onClick={saveOrder}>Create order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
